import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';
import { Vue } from 'vue-property-decorator';
import { i18n } from '@/main';
import Dictionary from '@/types/Dictionary';
import Pin from '@/types/FilterTree/Pin';
import TreeNode from '@/types/TreeNode';
import axios from 'axios';
import { API_URL } from '@/App.settings';
import PinTypeEnum from '@/enums/PinTypeEnum';
import BaseObjectTypeEnum from '@/enums/BaseObjectTypeEnum';
import ListItem from '@/types/fields/ListItem';
import FilterT from '@/types/FilterTree/FilterT';
import SelectModeEnum from '@/enums/SelectModeEnum';
import { ISelectModeStrategy } from './FilterTree/ISelectModeStrategy';
import { SelectModeStrategyFactory } from './FilterTree/SelectModeStrategyFactory';
import { reactive, markRaw } from 'vue';

//import { StoreExtended } from 'vuex-extensions';

/**
 * Store VueX dont la finalité est de gérer les filtres sur des noeuds de l'arborescence
 * Ce store est utilisable uniquement en préfixant avec le namespace FilterTree/ (car namespaced: true)
 */
@Module({
    namespaced: true,
    /** Pour permettre le module re-use (multi instances du module).
     * @see https://github.com/championswimmer/vuex-module-decorators */
    stateFactory: true
})
class FilterTreeStore extends VuexModule {
    /** Indique si le store médiator doit être utilisé ou non
     * @description Accès direct pour l'activer dans FilterTreeBuilder.ts pour les instances de FilterTree autre que MainFilterTree qui n'ont pas à utiliser le StoreMediator pour informer d'autres stores Vuex
     */
    private isStoreMediatorDisabled = false;

    /** Contient la liste des filtres existants id et name */
    private filters: ListItem[] = [] as ListItem[];

    /** Stratégie de mode de sélection de l'arborescence */
    private selectModeStrategy!: ISelectModeStrategy;

    /** filtre en cours sur l'arborescence */
    private filter: FilterT = {} as FilterT;

    /** Contient la liste des pins appliqués en cours */
    private pins: Pin[] = [] as Pin[];

    /** Id maximal d'un Pin */
    private maxPinId: number = 0;

    /** Contient la liste de tous les noeuds de l'arborescence */
    private nodes: TreeNode[] = [] as TreeNode[];

    /** Affiche uniquement les noeuds cochés */
    private isCheckOnly: boolean = false;

    /** Liste des noeuds impactés par la sélection (dans le cas d'une multi-sélections) */
    private involvedNodes: TreeNode[] = [] as TreeNode[];

    /** Affiche les noeuds dépréciés ou non */
    private isHistoricMode: boolean = false;

    /** Gestion regroupement global */
    private grouping: FilterT = {} as FilterT;

    /** Indique si pour le regroupement, on garde ou non les noeuds ancetres/parents */
    private keepAncestors: boolean = false;

    /** Indique si le composant HeritageTree doit être chargé */
    private heritageTreeAlive: boolean = false;

     /** Indique si le bouton mode historique est affiché ou non */
    private hasHistoricMode: boolean = false;

    /** Indique si le bouton de filtre par propriété est visible ou non */
    private hasPropertiesFilter: boolean = false;

    /** Indique si le bouton Seulement les éléments sélectionnés est affiché ou non */
    private hasToggleCheckOnly: boolean = false;

     /** Indique si le bouton tout sélectionné est affiché ou non */
    private hasSelectAll: boolean = false;

     /** Indique si le bouton tout supprimer est affiché ou non */
    private hasClearSelection: boolean = false;

    /** Indique si la fenêtre de filtre par propriété est affiché ou non */
    private propertiesFilterVisible: boolean = false;

    /** Liste de ObjectTypes affiché dans le propertie filter par défaut*/
    private propertiesFilterObjectTypes: string[] = undefined;

    /** Indique si il s'agit du périmetre utilisateur */
    private isUserPerimeter: boolean = true;

    /** Indique si le store médiator doit être utilisé ou non
     * @description Accès direct pour l'activer dans FilterTreeBuilder.ts pour les instances de FilterTree autre que MainFilterTree qui n'ont pas à utiliser le StoreMediator pour informer d'autres stores Vuex
     */
    @Mutation
    private setIsStoreMediatorDisabled(value: boolean) {
        this.isStoreMediatorDisabled = value;
    }

    @Mutation
    private setHeritageTreeAlive(value: boolean) {
        this.heritageTreeAlive = value;
    }

    @Mutation
    private setIsUserPerimeter(value: boolean) {
        this.isUserPerimeter = value;
    }

    /** Réinitialisation du store Vuex FilterTree */
    @Mutation
    private reset() {
        this.filters = [] as ListItem[];
        this.selectModeStrategy =  undefined;
        this.filter = {} as FilterT;
        this.pins = [] as Pin[];
        this.maxPinId = 0;
        this.nodes = [];
        this.isCheckOnly = false;
        this.isHistoricMode = false;
		this.grouping = {} as FilterT;
        this.involvedNodes = [] as TreeNode[];
        this.heritageTreeAlive = false;    
        this.isStoreMediatorDisabled = false;
        this.propertiesFilterObjectTypes = [];
    }

    @Mutation
    private setHistoricMode(isHistoricMode: boolean) {
        this.isHistoricMode = isHistoricMode;
    }

    @Mutation
    private setCheckOnly(isCheckOnly: boolean) {
        this.isCheckOnly = isCheckOnly;
    }

    @Mutation
    private setKeepAncestors(keepAncestors: boolean){
        this.keepAncestors = keepAncestors;
    }

    /** 
     * Application du regroupement
     * @param grouping Regroupement à appliquer
     **/
    @Mutation
    private setGrouping(grouping: FilterT = {} as FilterT) {
        this.grouping = grouping;
    }


    @Mutation
    private setButtonState({ hasHistoricMode, hasPropertiesFilter, hasToggleCheckOnly, hasSelectAll, hasClearSelection }) {
        if(hasHistoricMode != undefined)
            this.hasHistoricMode = hasHistoricMode;
        if (hasPropertiesFilter != undefined)
            this.hasPropertiesFilter = hasPropertiesFilter;
        if(hasToggleCheckOnly != undefined)
            this.hasToggleCheckOnly = hasToggleCheckOnly;
        if(hasSelectAll != undefined)
            this.hasSelectAll = hasSelectAll;
        if(hasClearSelection != undefined)
            this.hasClearSelection = hasClearSelection;
    }

    /** 
     * Application du regroupement
     * @param grouping Regroupement à appliquer
     **/
    @Mutation
    private forceGrouping() {
        if(this.filter && Object.keys(this.filter).length > 0 && this.filter.Id != '-1') {            
            this.filter.KeepAncestors = this.keepAncestors;

            this.grouping = this.filter;
        } 
        // Si pas de grouping mais qu'il y a des pins, on créé un grouping virtuel
        else if (this.pins.length > 0) {
            let groupingName = i18n.t("Grouping").toString();
            Vue.set(this, 'grouping', {
                    Id: "-1",
                    Name: groupingName,
                    Pins: [...this.pins],
                    Color: "",
                    Remark: "",
                    KeepAncestors: this.keepAncestors                   
                } as FilterT);
        }
        // Ici, c'est pour être sur qu'on envoi une objet vide coté serveur
        else {
            this.grouping = {} as FilterT;
        }

        this.filter = {} as FilterT;
    }

    @Mutation
    private clearFilter() {
        console.debug("FilterTree - clearFilter");
        this.filter = {} as FilterT;
    }

    /**
     * Ouvre un noeud ainsi que ses 
     * @param node              Node à ouvrir
     * @param scrollIntoView    Indique si l'arborescence doit être scrollée au niveau du noeud ou non (réalisé via le this.$store.subscribe dans HeritageTree qui scrute les mutations sur openNode)
     * @description NE SURTOUT PAS SUPPRIMER LE PARAMETRE scrollIntoView MEME S'IL N'EST PAS UTILISÉ ICI !!! ==> utilisé dans le payload de this.$store.subscribe d'HeritageTree.vue qui scrute les mutations sur openNode
     */
    @Mutation
    private openNode({ node, scrollIntoView }) {
        if (node !== undefined) {
            node.Expanded = true;
            for (let treeNode of this.nodes) {
                if (node.LeftBound > treeNode.LeftBound && node.RightBound < treeNode.RightBound) {
                    treeNode.Expanded = true;
                }
            }
        }
    }

    /** Passe la propriété expand de tout les noeuds à false */
    @Mutation
    private closeAllNode(){
        for (let treeNode of this.nodes) {
            treeNode.Expanded = false;
        }
    } 

    /**
     * Mise à jour du nom d'un noeud
     * Remarque : essentiellement utilisé lorsque le nom du noeud est modifié sur le formulaire objet pour éviter de recharger toute l'arborescence
     * @param nodeId : Id du noeud à mettre à jour
     * @param newName : Nouveau noeud à appliquer sur le noeud
     */
    @Mutation
    private updateNodeName({ nodeId, newName }) {
        let index = this.nodes.findIndex(x => x.Id === nodeId);
        if (index > -1)
            this.nodes[index].Name = newName;
    }
    
    /** 
     * Mise à jour des noeuds impactés
     * @param nodes Liste des noeuds à appliquer comme noeuds impactés
     */
    @Mutation
    private updateInvolvedNodes(nodes: TreeNode[]) {
        this.involvedNodes = nodes;
    }

    /** @todo : fait comme sur V2. Créer plutôt une seule méthode pour gérer appreciateNode/depreciateNode ? */
    @Mutation
    private appreciateNode({ nodeId }) {
        let index = this.nodes.findIndex(x => x.Id === nodeId);
        if (index > -1) {
            let node: TreeNode = (this.nodes[index] as TreeNode);
            node.Depreciated = false;	/** @todo : voir avec SLavina créer une propriété isDepreciated sur le TreeNode ? */
            node.Hidden = false;

            // Apprécie les enfants
            let lNodes: TreeNode[] = [];
            for (let treeNode of this.nodes) {
                if ((node.Depth + 1 == treeNode.Depth)
                    && (node.LeftBound < treeNode.LeftBound)
                    && (node.RightBound > treeNode.RightBound)) {

                    treeNode.Depreciated = false;
                    treeNode.Hidden = false;
                }
            }
        }
    }

    /** @todo : fait comme sur V2. Créer plutôt une seule méthode pour gérer appreciateNode/depreciateNode ? */
    @Mutation
    private depreciateNode({ nodeId, hideNode }) {
        let index = this.nodes.findIndex(x => x.Id === nodeId);
        if (index > -1) {
            let node: TreeNode = (this.nodes[index] as TreeNode);
            node.Depreciated = true;	/** @todo : voir avec SLavina créer une propriété isDepreciated sur le TreeNode ? */

            // Affiche ou masque le noeud selon que l'on soit en mode Historique ou non
            node.Hidden = hideNode;

            // Dépréciation des enfants de ce noeud
            for (let treeNode of this.nodes) {
                if ((node.Depth + 1 == treeNode.Depth)
                    && (node.LeftBound < treeNode.LeftBound)
                    && (node.RightBound > treeNode.RightBound)) {

                    treeNode.Depreciated = true;
                    treeNode.Hidden = hideNode;
                }
            }
        }
    }

    @Mutation
    private loadFilters({ filters }) {
        this.filters = filters;
    }

    @Mutation
    private loadFilter(filter: FilterT) {
        if(this.filter.Id != "-1")
            this.filter = filter;
        else
            this.filter = {} as FilterT;
    }

    @Mutation
    private setBases({ nodes = undefined, pins = undefined, maxPinId = undefined }) {
        if(nodes)
            Vue.set(this, 'nodes', nodes);
        if(pins)
            Vue.set(this, 'pins', pins);
        if (maxPinId)
            Vue.set(this, 'maxPinId', maxPinId);
    }

    /** Mise à jour du mode sélection de l'arborescence */
    @Mutation
    private setSelectModeStrategy(value) {
        this.selectModeStrategy = value;
    }

    /** Mise à jour de l'affichage ou non de la fenêtre de Filtre par propriété */
    @Mutation
    public setPropertiesFilterVisible(value) {
        this.propertiesFilterVisible = value;
    }

    /**  Mise à jour de l'affichage des object types proposé dans le property filter */
    @Mutation
    public setPropertiesFilterObjectTypes(filteredObjectTypes: string[]){
        this.propertiesFilterObjectTypes = filteredObjectTypes;
    }

    /** Indique si le store médiator doit être utilisé ou non
     * @description Accès direct pour l'activer dans FilterTreeBuilder.ts pour les instances de FilterTree autre que MainFilterTree qui n'ont pas à utiliser le StoreMediator pour informer d'autres stores Vuex
     */
    @Action({ rawError: true })
    public SetIsStoreMediatorDisabled(value: boolean) {
        this.context.commit('setIsStoreMediatorDisabled', value);
    }

    /** Supprime le filter et ses pins sélectionnés */
    @Action({ rawError: true })
    public RemoveFilter() {
        this.context.commit('clearFilter');
        this.selectModeStrategy.RemoveAllPins([...this.nodes], this.isHistoricMode, this.isCheckOnly);
    }

    /** Supprime le filter, mais laisse les pins sélectionnés */
    @Action({ rawError: true })
    public ClearFilter() {
        this.context.commit('clearFilter');
    }

    @Action({ rawError: true })
    public RemoveGrouping() {
        this.context.commit('setGrouping');
        this.selectModeStrategy.RemoveAllPins([...this.nodes], this.isHistoricMode, this.isCheckOnly);
    }

    @Action({ rawError: true})
    public async SetPins( pins: Pin[] ){
        this.context.dispatch("ShowLoadingMask", i18n.t("LoadingSelection"), { root: true });     // "Chargement de la sélection"
        this.selectModeStrategy.SetPins([...this.nodes], pins, this.isHistoricMode, this.isCheckOnly, true);
        this.context.dispatch("HideLoadingMask", {}, { root: true });
    }

    @Action({ rawError: true })
    public AddPin({ included, type, reference, selections, text, depreciated, objectType = BaseObjectTypeEnum.None, treeNode = undefined, criterias = [], subFilters = []}) {
        /** @todo : Ajouter une remontée d'erreur pour l'appelant si le filtre existe déjà */
        /** @todo : Accès base de données pour certain type de */
        let nodes: TreeNode[] = [...this.nodes];
        let pins: Pin[] = [...this.pins];

        let pin: Pin = {
            Id: this.maxPinId + 1,
            Included: included, 
            Type: type,
            ObjectType: treeNode && Object.keys(treeNode).length > 0 ? treeNode.ObjectType : BaseObjectTypeEnum.None,
            Reference: reference,
            Selections: selections, 
            Text: text, 
            Depreciated: depreciated, 
            TreeNode: treeNode,
            Filter : {
                ObjectType: objectType,
                Criterias: criterias, 
                SubFilters: subFilters
            }
        };
        
        console.debug("FilterTree - AddPin - pin.Id : %O", pin.Id);  

        if (objectType != BaseObjectTypeEnum.None || type == PinTypeEnum.Root) {
            this.context.dispatch("ShowLoadingMask", i18n.t("LoadingSelection"), { root: true });     // "Chargement de la sélection"

            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
            args["Pin"] = pin;
            args["Grouping"] = this.grouping;

            axios.post(API_URL + "FilterTree/FilterProperties", args).then((response) => {
                if (response.data) {
                    pin.Selections = response.data.Item2;
                    pin.Text = response.data.Item1;
                    pins.push(pin);
                    this.selectModeStrategy.SetPins(nodes, pins, this.isHistoricMode, this.isCheckOnly, true);
                    this.context.dispatch("HideLoadingMask", {}, { root: true });
                }
            }).catch(err => {
                this.context.dispatch("HideLoadingMask", {}, { root: true });
                throw err;
            });
        } else {
            this.context.dispatch("ShowLoadingMask", i18n.t("LoadingSelection"), { root: true });       // "Chargement de la sélection"
            pins.push(pin);
            this.selectModeStrategy.SetPins(nodes, pins, this.isHistoricMode, this.isCheckOnly, true);
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Défini le noeud sélectionné dans l'arborescence
     * @param node                  Noeud à sélectionner
     * @param openNode              Indique si les noeuds  du noeud sont à ouvrir ou non
     * @param scrollIntoView        Indique si l'arborescence doit être scrollée au niveau du noeud ou non (réalisé via le this.$store.subscribe dans HeritageTree qui scrute les mutations sur openNode)
     */
    @Action({ rawError: true })
    public SelectNode({ node, openNode = false, scrollIntoView = true }) {
        this.selectModeStrategy.SelectNodes([node], !node.Checked, [...this.nodes], [...this.pins], this.maxPinId + 1, this.isHistoricMode, this.isCheckOnly);

        // Ouverture du noeud
        if (openNode)
            this.context.commit('openNode', { node, scrollIntoView });
    }

    /** 
     * Coche une liste de noeuds
     * @param nodeIds Liste d'id de noeuds à cocher
     * @todo APROUILLAC à supprimer + tard
     */
    @Action({ rawError: true })
    public CheckNodeIds(nodeIds: string[]) {
        console.debug("FilterTree - CheckNodeIds");
        let nodes: TreeNode[] = [...this.nodes];
        let selectedNodes: TreeNode[] = [];

        for (let nodeId of nodeIds) {
            let nodeIndex: number = nodes.findIndex(n => n.Id == nodeId);
            if (nodeIndex > -1)
                selectedNodes.push(nodes[nodeIndex]);
        }

        this.selectModeStrategy.SelectNodes(selectedNodes, true, nodes, [], this.maxPinId + 1, this.isHistoricMode, this.isCheckOnly);
    }

    /**
     * 
     * @param param0 
     */
    @Action({ rawError: true })
    public UpdatePin({ id, treeNode, objectType, criterias, subFilters }){
        let pins: Pin[] = [...this.pins];
        let nodes: TreeNode[] = [...this.nodes];

        this.context.dispatch("ShowLoadingMask", i18n.t('FilteringUpdate').toString(), { root: true });    // "Mise à jour du filtre"

        let pin = pins.find(x => x.Id == id);
        pin.Reference = treeNode && Object.keys(treeNode).length > 0 ? treeNode.Id : null;
        pin.ObjectType = treeNode && Object.keys(treeNode).length > 0 ? treeNode.ObjectType : BaseObjectTypeEnum.None;
        pin.Filter = {
            ObjectType : objectType,
            Criterias : criterias,
            SubFilters : subFilters
        }

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["Pin"] = pin;
        args["Grouping"] = this.grouping;

        // Axios pour regénérer le texte affiché et la liste des noeuds correspondant
        axios.post(API_URL + "FilterTree/FilterProperties", args).then((response) => {
            if (response.data) {
                pin.Text = response.data.Item1;
                pin.Selections = response.data.Item2;
                this.selectModeStrategy.SetPins(nodes, pins, this.isHistoricMode, this.isCheckOnly, true);
                this.context.dispatch("HideLoadingMask", {}, { root: true });
            }
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    @Action({ rawError: true })
    public RemovePin(pin: Pin) {
        this.context.dispatch("ShowLoadingMask", i18n.t('DeletingSelection').toString(), { root: true });   // "Suppression de la sélection"

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        axios.post(API_URL + "FilterTree/RefreshEmpty", args).then((response) => {
            this.selectModeStrategy.RemovePins([pin], [...this.nodes], [...this.pins], this.isHistoricMode, this.isCheckOnly);
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    @Action({ rawError: true })
    public async RemoveAllPins() {
        this.context.dispatch("ShowLoadingMask", i18n.t('ClearingAllSelections').toString(), { root: true }); // "Suppression de toutes sélections"

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        await axios.post(API_URL + "FilterTree/RefreshEmpty", args).then((response) => {
            this.selectModeStrategy.RemoveAllPins([...this.nodes], this.isHistoricMode, this.isCheckOnly);
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    @Action({ rawError: true })
    public SelectAllNodes() {
        this.context.dispatch("ShowLoadingMask", i18n.t('ClearingAllSelections').toString(), { root: true });   // "Selections de toutes les Pins de premiers niveaux"

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        axios.post(API_URL + "FilterTree/RefreshEmpty", args).then((response) => {
            this.selectModeStrategy.SelectAllNodes([...this.nodes], this.isHistoricMode, this.isCheckOnly);
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    @Action({ rawError: true })
    public ToggleIncluded(pin: Pin) {
        console.debug("FilterTree - ToggleIncluded : " + pin);
        this.context.dispatch("ShowLoadingMask", i18n.t('InvertingSelection').toString(), { root: true });  // "Inversion de la sélection..."

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        axios.post(API_URL + "FilterTree/RefreshEmpty", args).then((response) => {
            this.selectModeStrategy.ToggleIncluded(pin, [...this.nodes], [...this.pins], this.isHistoricMode, this.isCheckOnly);
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    @Action({ rawError: true })
    public SetHeritageTreeAlive(value): void {
        this.context.commit("setHeritageTreeAlive", value);
    }

    /** Chargement de l'arborescence standard lié au périmetre utilisateur 
     * @param pins              Liste de pins à appliquer sur l'arborescence après son chargement
     * @param enableGroup       Permet de désactivé le rechargement du grouping sélectionné pour le moment
     * @param showLoadingMask   Indique si le message de chargement de l'arborescence doit être affiché ou non
     * @param configTree        Indique une instance de classe implémentant l'interface IConfigTree pour un filtrage spécifique des noeuds
     * @param selectMode        Mode de sélection de l'arborescence
     * */
    @Action({ rawError: true })
    public async LoadData({ selectMode = undefined, isUserPerimeter = true, pins = [], enableGroup = true, showLoadingMask = true, configTree = undefined }) {
        // Requête le server pour obtenir la liste des noeuds
        if (showLoadingMask)
            this.context.dispatch("ShowLoadingMask", i18n.t('LoadingTree').toString(), { root: true });    // "Chargement arborescence..."

        this.context.commit("setHeritageTreeAlive", false);
        this.context.commit("setIsUserPerimeter", isUserPerimeter);
        this.context.commit('setBases', { nodes: [] as TreeNode[] });
        
        let grouping : FilterT = this.grouping;

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["IsUserPerimeter"] = this.isUserPerimeter;
        args["Grouping"] = (enableGroup) ? grouping : {} as FilterT;

        try {
            let response = await axios.post(API_URL + "FilterTree/GetHeritageNodes", args)
            if (response && response.data) {
                let nodes: TreeNode[] = response.data.map(node => {
                    // ÉTAPE 1 : On crée un objet qui contient UNIQUEMENT ce qui doit bouger.
                    const reactiveNode = reactive({
                        Name: node.Name,
                        Invariant: node.Invariant,
                        Checked: node.Checked,
                        Selected: node.Selected,
                        Indeterminate: node.Indeterminate,
                        CheckLeader: node.CheckLeader,
                        NestedChecked: node.NestedChecked,
                        IsAncestor: node.IsAncestor,
                        Expanded: node.Expanded,
                        Forced: node.Forced,
                        Hidden: node.Hidden, 
                        WithCheckBox: node.WithCheckBox,
                        HiddenCaret: node.HiddenCaret,
                        Disabled: node.Disabled,
                        Depreciated: node.Depreciated
                    });
                    // ÉTAPE 2 : On prépare les données statiques.
                    const staticData = {
                        Id: node.Id,
                        Reference: node.Reference,
                        Icon: node.Icon,
                        ObjectType: node.ObjectType,
                        LeftBound: node.LeftBound,
                        RightBound: node.RightBound,
                        Depth: node.Depth,
                        IsEditable: node.IsEditable, 
                        // Selections est un Tableau (Objet), donc ici markRaw est utile et fonctionne !
                        Selections: markRaw(node.Selections) 
                    };

                    // ÉTAPE 3 : La Fusion Magique
                    return Object.assign(reactiveNode, staticData) as TreeNode;
                });

                let isHistoricMode : boolean = true;
                let isCheckOnly : boolean = false;
                let cleanNodes : boolean = true;

                await this.context.dispatch('CreateSelectModeStrategy', { selectMode, nodes, pins, isHistoricMode, isCheckOnly, configTree, cleanNodes, grouping });
            }
            
            this.context.commit("setHeritageTreeAlive", true);

            if (showLoadingMask)
                this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
        catch(err) {
            if (showLoadingMask)
                this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        };
    }

    /** Dans le contextMenu méthode qui est appeler pour sélectionné ou déselectionné des types en dessous d'un noeud */
    @Action({ rawError: true })
    public async NodesByObjectType ({ node, objectType, checked }) {
        node.Expanded = true;
        
        let treeNodes: TreeNode[] = [];

        for (let treeNode of this.nodes) {
            if ((treeNode.ObjectType == objectType) && (node.LeftBound < treeNode.LeftBound) 
                && (node.RightBound > treeNode.RightBound)) {
                    treeNodes.push(treeNode);
            }
        }

        if (treeNodes && treeNodes.length > 0){
            this.selectModeStrategy.SelectNodes(treeNodes, checked, [...this.nodes], [...this.pins], this.maxPinId, this.isHistoricMode, this.isCheckOnly);
        }
    }

    /** Application du regroupement */
    @Action({ rawError: true })
    public async SetGrouping(grouping: FilterT) {
        this.context.commit("setGrouping", grouping);
    }

    /** Ici, on force l'application d'un regroupement et des pins sélectionnées */
    @Action({ rawError: true })
    public async ForceGrouping() {
        this.context.commit("forceGrouping");
    }

    /** Initialise l'instance courante de FilterTree à partir d'une autre instance
     * @param filterTreeStore   Store Vuex dont le state doit être cloné
     * @param pins              Includes à initialiser après copie
     * @param grouping          Indique un regroupement à appliquer après clonage
     * @param cleanNodes        Réinitialise les états de l'ensemble des noeuds
     * @param showLoadingMask   Indique si le masque de chargement est affiché ou non
     * @param configTree        Indique une instance de classe implémentant l'interface IConfigTree pour un filtrage spécifique des noeuds
     */
    @Action({ rawError: true })
    public async CloneTree({ selectMode, filterTreeStore, pins = [], grouping = undefined, cleanNodes = false, showLoadingMask = true, configTree = undefined }) {
        let isHistoricMode: boolean = this.context.rootState[filterTreeStore].isHistoricMode;
        this.context.commit("setHistoricMode", isHistoricMode);
        let isCheckOnly: boolean = this.context.rootState[filterTreeStore].isCheckOnly;

        if (showLoadingMask)
            this.context.dispatch("ShowLoadingMask", i18n.t('LoadingTree').toString(), { root: true });
            
        this.context.commit("setHeritageTreeAlive", false);
        this.context.commit('setBases', { nodes: [] as TreeNode[] });

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        try {
            /** Va chercher l'instance d'arborescence en paramètre pour récupérer sa liste de noeud afin de la copier dans l'instance d'arborescence courante */
            let nodes: TreeNode[] = JSON.parse(JSON.stringify(this.context.rootState[filterTreeStore].nodes)).map(node => {
                // ÉTAPE 1 : On crée un objet qui contient UNIQUEMENT ce qui doit bouger.
                const reactiveNode = reactive({
                    Name: node.Name,
                    Invariant: node.Invariant,
                    Checked: node.Checked,
                    Selected: node.Selected,
                    Indeterminate: node.Indeterminate,
                    CheckLeader: node.CheckLeader,
                    NestedChecked: node.NestedChecked,
                    Expanded: node.Expanded,
                    Forced: node.Forced,
                    Hidden: node.Hidden, 
                    WithCheckBox: node.WithCheckBox,
                    HiddenCaret: node.HiddenCaret,
                    Disabled: node.Disabled,
                    Depreciated: node.Depreciated
                });
                // ÉTAPE 2 : On prépare les données statiques.
                const staticData = {
                    Id: node.Id,
                    Reference: node.Reference,
                    Icon: node.Icon,
                    ObjectType: node.ObjectType,
                    LeftBound: node.LeftBound,
                    RightBound: node.RightBound,
                    Depth: node.Depth,
                    IsEditable: node.IsEditable,
                    // Selections est un Tableau (Objet), donc ici markRaw est utile et fonctionne !
                    Selections: markRaw(node.Selections) 
                };

                // ÉTAPE 3 : La Fusion Magique
                return Object.assign(reactiveNode, staticData) as TreeNode;
            });

            if (nodes) 
                await this.context.dispatch('CreateSelectModeStrategy', { selectMode, nodes, pins, isHistoricMode, isCheckOnly, configTree, cleanNodes, grouping });
            else {
                console.error("Le store VueX %s passé en paramètre ne comporte aucun noeud !", filterTreeStore);
                throw "Le store VueX %s passé en paramètre ne comporte aucun noeud !";
            }
            
            this.context.commit("setHeritageTreeAlive", true);
        }
        catch(err) {
            throw err;
        }
        finally {
            if (showLoadingMask)
                this.context.dispatch("HideLoadingMask", {}, { root: true });
        }
    }

    /**
     * Rafraichit la liste des noeuds en les obtenant à nouveau du server + réapplique les filtres sur ces noeuds
     * @param nodeIdToOpen  Noeud à ouvrir après le rafraichissement de l'arborescence ==> si typeScript alors contient le PersistentId sinon si type TreeNode alors il s'agit du noeud complet
     * @param selectNode    Insique si le noeud à ouvrir doit être sélectionné ou non
     * @param preview       Indique si l'on est en mode prévisualisation ou non
     */
    @Action({ rawError: true })
    public async Refresh({ nodeIdToOpen = "", selectNode = true, preview = false }) {
        // Requête le server pour obtenir la liste des noeuds
        this.context.dispatch("ShowLoadingMask", i18n.t('LoadingTree').toString(), { root: true });    // "Chargement arborescence..."
        this.context.commit("setHeritageTreeAlive", false);
        this.context.commit('setBases', { nodes: [] as TreeNode[] });

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["IsUserPerimeter"] = this.isUserPerimeter;
        args["Grouping"] = this.grouping;

        await axios.post(API_URL + "FilterTree/GetHeritageNodes", args).then((response) => {
            if (response && response.data) {

                
                let nodes: TreeNode[] = response.data.map(node => {
                    // ÉTAPE 1 : On crée un objet qui contient UNIQUEMENT ce qui doit bouger.
                    const reactiveNode = reactive({
                        Name: node.Name,
                        Invariant: node.Invariant,
                        Checked: node.Checked,
                        Selected: node.Selected,
                        Indeterminate: node.Indeterminate,
                        CheckLeader: node.CheckLeader,
                        NestedChecked: node.NestedChecked,
                        Expanded: node.Expanded,
                        Forced: node.Forced,
                        Hidden: node.Hidden, 
                        WithCheckBox: node.WithCheckBox,
                        HiddenCaret: node.HiddenCaret,
                        Disabled: node.Disabled,
                        Depreciated: node.Depreciated
                    });
                    // ÉTAPE 2 : On prépare les données statiques.
                    const staticData = {
                        Id: node.Id,
                        Reference: node.Reference,
                        Icon: node.Icon,
                        ObjectType: node.ObjectType,
                        LeftBound: node.LeftBound,
                        RightBound: node.RightBound,
                        Depth: node.Depth,
                        IsEditable: node.IsEditable, 
                        // Selections est un Tableau (Objet), donc ici markRaw est utile et fonctionne !
                        Selections: markRaw(node.Selections) 
                    };

                    // ÉTAPE 3 : La Fusion Magique
                    return Object.assign(reactiveNode, staticData) as TreeNode;
                });

                this.selectModeStrategy.Init(nodes, [], this.isHistoricMode, this.isCheckOnly, undefined, true, this.grouping);
                
                console.debug("FilterTree - Refresh - Après this.selectModeStrategy.Init()");

                // Ouvre et sélectionne le noeud cible dans l'arborescence
                if (nodeIdToOpen.toString() !== '') {
                    // Recherche du noeud
                    let nodeIndex: number = this.nodes.findIndex(x => x.Id === String(nodeIdToOpen));
                    if (nodeIndex > -1) {
                        let node: TreeNode = this.nodes[nodeIndex];

                        /** Si l'on est pas en mode historique et que le noeud est déprécié alors on va ouvrir le parent */
                        if (!this.isHistoricMode && node.Depreciated)
                            node = this.context.getters['ParentNode']({ childNodeId: nodeIdToOpen, firstNotDepreciated: true });

                        // Ouverture du noeud
                        if (node.ObjectType !== "Client") {
                            this.selectModeStrategy.SelectNodes([node], true, [...this.nodes], [...this.pins], this.maxPinId, this.isHistoricMode, this.isCheckOnly);
                            this.context.commit('openNode', { node: node, scrollIntoView: true });
                        }
                    }
                }
            }
            this.context.commit("setHeritageTreeAlive", true);
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    /**
     * Ouvre un noeud ainsi que ses 
     * @param node              Node à ouvrir
     * @param scrollIntoView    Indique si l'arborescence doit être scrollée au niveau du noeud ou non (réalisé via le this.$store.subscribe dans HeritageTree qui scrute les mutations sur openNode)
     */
    @Action({ rawError: true })
    public OpenNode({ node, scrollIntoView = true }): void {
        this.context.commit('openNode', { node, scrollIntoView });
    }

    /** Passe la propriété expand de tout les noeuds à false */
    @Action({ rawError: true })
    public CloseAllNode(): void {
        this.context.commit('closeAllNode');
    }

    /**
     * Mise à jour du nom d'un noeud
     * Remarque : essentiellement utilisé lorsque le nom du noeud est modifié sur le formulaire objet pour éviter de recharger toute l'arborescence
     * @param nodeId : Id du noeud à mettre à jour
     * @param newName : Nouveau noeud à appliquer sur le noeud
     */
    @Action({ rawError: true })
    public UpdateNodeName({ nodeId, newName }): void {
        this.context.commit('updateNodeName', { nodeId, newName });
    }

    @Action({ rawError: true })
    public AppreciateNode({ nodeId }): void {
        this.context.commit('appreciateNode', { nodeId });
    }

    @Action({ rawError: true })
    public DepreciateNode({ nodeId }): void {
        let hideNode: boolean = true;
        if (this.isHistoricMode)
            hideNode = false;

        this.context.commit('depreciateNode', { nodeId, hideNode });
    }

    /** 
     * Fonction associée au bouton "Afficher la sélection"
     * Bascule entre l'affichage uniquement des noeuds sélectionnés ou de tous les noeuds
     **/
    @Action({ rawError: true })
    public ToggleCheckOnly(): void {
        this.context.commit('setCheckOnly', !this.isCheckOnly);    
        this.selectModeStrategy.SetCheckOnly([...this.nodes], this.isHistoricMode, this.isCheckOnly);
    }

    /** Rafraichit la liste des pins */
    @Action({ rawError: true })
    public async RefreshFilter() {
        console.debug("FilterTree - RefreshFilter : ");
        this.context.dispatch("ShowLoadingMask", i18n.t('SwitchingHistoric').toString(), { root: true });     // "Changement Historique"

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        axios.post(API_URL + "FilterTree/RefreshEmpty", args).then((response) => {
            if (!this.isHistoricMode) {
                let tempPins: Pin[] = [];
                for (let pin of this.pins) {
                    if (pin.Type == PinTypeEnum.Tree && pin.Depreciated) {
                        tempPins.push(pin);
                    }
                }
                // On vient de créer une liste des pins de type ARBO et dépréciés
                // On parcourt la liste que l'on vient de créer
                for (let tempPin of tempPins) {
                    let pinIndex = this.pins.findIndex(x => x.Id === tempPin.Id);
                    this.pins.splice(pinIndex, 1);
                }
            }

            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    @Action({ rawError: true })
    public async LoadFilters() {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        await axios.post<ListItem[]>(API_URL + "FilterTree/LoadFilters", args).then((response) => {
            if (response && response.data) {
                let data: ListItem[] = response.data;
                this.context.commit('loadFilters', { filters: data });
            }
        });
    }

    @Action({ rawError: true })
    public async UpdateFilter(filter: FilterT) {
        this.selectModeStrategy.SetPins([...this.nodes], filter.Pins, this.isHistoricMode, this.isCheckOnly, true);
        this.context.commit('loadFilter', filter);
    }

    @Action({ rawError: true })
    public async LoadFilter({ filter = {} }) {
        this.context.dispatch("ShowLoadingMask", i18n.t('LoadingPerimeter').toString(), { root: true });    // "Chargement du périmètre"

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["Grouping"] = this.grouping;
        args["Filter"] = filter;

        await axios.post<FilterT>(API_URL + "FilterTree/Load", args).then((response) => {
            if (response && response.data) {
                let data: FilterT = response.data;
                this.context.commit('loadFilter', data);
                this.selectModeStrategy.SetPins([...this.nodes], data.Pins, this.isHistoricMode, this.isCheckOnly, true);
            }
            this.context.dispatch("HideLoadingMask", {}, { root: true });
        }).catch(err => {
            this.context.dispatch("HideLoadingMask", {}, { root: true });
            throw err;
        });
    }

    /**
     * Permet la suppression du filtre sélectionné
     * @param filterId identifiant du filtre à supprimer
     */
    @Action({ rawError: true })
    public async Delete(filterId) {
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["Id"] = filterId;

        console.debug("FilterTree - Delete");

        await axios.post(API_URL + "FilterTree/Delete", args);
    }

    /** 
     * Passe en mode historique
     * @param [value = false] Etat de l'historique à affecter
     */
    @Action({ rawError: true })
    public SetHistoricMode(value = false) {
        this.context.commit('setHistoricMode', value);
        this.selectModeStrategy.SetHistoricMode([...this.nodes], [...this.pins], this.isHistoricMode, this.isCheckOnly);
    }

    /** Bascule le mode historique */
    @Action({ rawError: true })
    public ToggleHistoricMode() {
        this.context.dispatch('SetHistoricMode', !this.isHistoricMode);
    }

    @Action({ rawError: true})
    public SetKeepAncestors(value = false){
        this.context.commit('setKeepAncestors', value);
    }

    /** Réinitialisation du store Vuex FilterTree */
    @Action({ rawError: true })
    public Reset() {
        this.context.commit("reset");
    }

    @Action({ rawError: true })
    public CreateSelectModeStrategy({ selectMode, nodes, pins, isHistoricMode, isChekOnly, configTree, cleanNodes, grouping }) {
        let selectModeStrategy: ISelectModeStrategy = SelectModeStrategyFactory.CreateInstance(selectMode, this);
        this.context.commit('setSelectModeStrategy', selectModeStrategy);
        this.selectModeStrategy.Init(nodes, pins, isHistoricMode, isChekOnly, configTree, cleanNodes, grouping);
    }

    @Action({ rawError: true })
    public ChangeSelectModeStrategy({ selectMode, configTree = undefined }) {
        let selectModeStrategy: ISelectModeStrategy = SelectModeStrategyFactory.CreateInstance(selectMode, this); 
        this.context.commit('setSelectModeStrategy', selectModeStrategy);
        this.selectModeStrategy.Init([...this.nodes], [...this.pins], this.isHistoricMode, this.isCheckOnly, configTree, true, undefined);
    }

    /** Mise à jour de l'affichage ou non de la fenêtre de Filtre par propriété */
    @Action({ rawError: true })
    public SetPropertiesFilterVisible(value) {
        this.context.commit('setPropertiesFilterVisible', value);
    }

    /**  Mise à jour de l'affichage des object types proposé dans le property filter undefined pour réinitialiser le filtre */
    @Action({ rawError: true })
    public SetPropertiesFilterObjectTypes(filteredObjectTypes: string[]){
        this.context.commit('setPropertiesFilterObjectTypes', filteredObjectTypes);
    }

    /** Indique si le store médiator doit être utilisé ou non
     * @description Accès direct pour l'activer dans FilterTreeBuilder.ts pour les instances de FilterTree autre que MainFilterTree qui n'ont pas à utiliser le StoreMediator pour informer d'autres stores Vuex
     */
    public get GetIsStoreMediatorDisabled(): boolean {
        return this.isStoreMediatorDisabled;
    }

    /** Indique si le composant HeritageTree doit être chargé */
    public get GetHeritageTreeAlive(): boolean {
        return this.heritageTreeAlive;
    }

    /** Renvoie l'id le plus élevé de la série de pins */
    public get GetMaxPinId(): number{
        return this.maxPinId;
    }

    /** Renvoi la liste des pins présente sur le filtre en cours d'utilisation */
    public get GetFilter(): FilterT {
        return this.filter;
    }

    /** Renvoi le noeud le plus haut dans l'arborescence en cours (le client) */
    public get RootNode(): TreeNode {
        return this.nodes[0];
    }

    /**
     * Renvoi les noeuds de premier niveau juste aprs le client
     * @param node noeud dont les enfants doivent être retourné
     */
    public get SelectedNodes(): TreeNode[] {

        let lNodes: TreeNode[] = [];
        let leftBound = 1;
        for (let treeNode of this.nodes) {
            if (treeNode.LeftBound > leftBound) {
                lNodes.push(treeNode);
                leftBound = treeNode.RightBound;
            }
        }
        
        return lNodes.sort((a, b) => {
            if (a.ObjectType === b.ObjectType) {
                return a.Name.localeCompare(b.Name); // Tri alphabétique correct
            }
            return b.ObjectType.localeCompare(a.ObjectType); // Tri inversé par type
        });
    }

    /**
     * Renvoi les noeuds enfants d'un noeud donné
     * @param node noeud dont les enfants doivent être retourné
     * Attention a ce que les noeuds soient bien triés par le leftbound
     */
    public get ChildNodes() {
        return (node: TreeNode): TreeNode[] => {
            let lNodes: TreeNode[] = [];
            for (let treeNode of this.nodes) {
                if ((node.Depth + 1 == treeNode.Depth)
                    && (node.LeftBound < treeNode.LeftBound)
                    && (node.RightBound > treeNode.RightBound)) {

                    lNodes.push(treeNode);
                }

                if (treeNode.LeftBound > node.RightBound) {
                    break; 
                }
            }

            return lNodes.sort((a, b) => {
                if (a.ObjectType === b.ObjectType) {
                    return a.Name.localeCompare(b.Name); // Tri alphabétique correct
                }
                return b.ObjectType.localeCompare(a.ObjectType); // Tri inversé par type
            });
        }
    }

    /** Retourne tout les pins de sélection de l'arborescence */
    public get GetPins(): Pin[]{
        return this.pins;
    }
    
    /**
     * Renvoi les noeuds descendants d'un noeud donné
     * @param node noeud dont les enfants doivent être retourné
     */
    public get DescObjectTypes() {
        return (node: TreeNode, withCheckBox: boolean): string[] => {
            let objectTypes: string[] = [];

            for (let treeNode of this.nodes) {
                if (((withCheckBox && treeNode.WithCheckBox) || !withCheckBox) 
                    && (node.LeftBound < treeNode.LeftBound) 
                    && (node.RightBound > treeNode.RightBound)) {
                        if (!objectTypes.includes(treeNode.ObjectType))
                            objectTypes.push(treeNode.ObjectType);
                }

                if (treeNode.LeftBound > node.RightBound) {
                    break; 
                }
            }

            return objectTypes;
        }
    }

    /** 
     * Retourne le noeud parent d'un Node
     * @param childNodeId           Noeud enfant pour lequel on doit rechercher le parent
     * @param firstNotDepreciated   Indique si on retourne le premier parent non déprécié
     */
    public get ParentNode() {
        return ({ childNodeId, firstNotDepreciated = false }): TreeNode => {
            let lNode: TreeNode = {} as TreeNode;

            // Recherche du noeud
            let nodeIndex: number = this.nodes.findIndex(x => x.Id === String(childNodeId));
            if (nodeIndex !== -1) {
                let node: TreeNode = this.nodes[nodeIndex];

                for (let treeNode of this.nodes) {
                    if (treeNode.Depth + 1 == node.Depth && node.LeftBound > treeNode.LeftBound 
                        && node.RightBound < treeNode.RightBound 
                        && (!firstNotDepreciated || (firstNotDepreciated && !(treeNode.Depreciated)))) {
                        lNode = treeNode;
                        break;
                    }
                }
            }

            return lNode;
        }
    }

    /** 
     * Retourne le noeud ancestre d'un Node
     * @param childNodeId   Noeud enfant pour lequel on doit rechercher le parent
     * @param prefixType    Préfixe attendu du noeud parent ('BO', 'LBO', 'Equipment', ...)
     */
    public get AncestorNode() {
        return ({ childNodeId, prefixType }): TreeNode => {
            let lNode: TreeNode = {} as TreeNode;

            // Recherche du noeud
            let nodeIndex: number = this.nodes.findIndex(x => x.Id === String(childNodeId));

            if (nodeIndex !== -1) {
                let node: TreeNode = this.nodes[nodeIndex];

                for (let treeNode of this.nodes) {
                    if (node.LeftBound > treeNode.LeftBound && node.RightBound < treeNode.RightBound) {
                        if (prefixType) { 
                            const regexp = new RegExp('^' + prefixType + '\_*');

                            if (regexp.test(treeNode.ObjectType)) {
                                lNode = treeNode;
                                break;
                            }
                        }
                        else {
                            lNode = treeNode;
                            break;
                        }
                    }
                }
            }

            return lNode;
        }
    }

    /** Retourne le node correspondant à un Id de noeud */
    public get Node() {
        return (nodeId: string): TreeNode => {
            let node: TreeNode = {} as TreeNode;

            // Recherche du noeud
            let nodeIndex: number = this.nodes.findIndex(x => x.Id === String(nodeId));
            if (nodeIndex !== -1)
                node = this.nodes[nodeIndex];

            return node;
        }
    }

    /** Retourne l'Id de noeud parent d'un Node
     * @param childNodeId           Noeud enfant pour lequel on doit rechercher le parent
     * @param firstNotDepreciated   Indique si on retourne le premier parent non déprécié
     */
    public get ParentNodeId() {
        return ({ childNodeId, firstNotDepreciated }): string => {
            return this.ParentNode({ childNodeId: childNodeId, firstNotDepreciated: firstNotDepreciated }).Id;
        }
    }

    /**
     * Obtient les noeuds  d'un noeud
     * @returns Tableaux de TreeNode
     */
    public get GetNodePath() {
        return (node: TreeNode): TreeNode[] => {

            let lNodes: TreeNode[] = [];
            for (let treeNode of this.nodes) {
                if ((node.LeftBound > treeNode.LeftBound)
                    && (node.RightBound < treeNode.RightBound)
                    && (treeNode.Depth >= 1)) {	//  && treeNode.ObjectType.substring(1, 2) != 'BO'
                    lNodes.push(treeNode);
                }
            }

            return lNodes;
        }
    }

    /** Retourne l'affichage ou non de la sélection */
    public get IsCheckOnly(): boolean {
        return this.isCheckOnly;
    }

    /**
     * Liste des filtres existants
     * @param lastFilters Nombre de derniers filtres à retourner
     */
    public get GetFilters() {
        return (lastFilters: number = undefined): ListItem[] => {
            if (lastFilters && lastFilters <= this.filters.length) {
                /** Création d'une variable locale pour éviter le reactive dans le rendu avec une erreur d'infinite loop */
                let localFilters = [...this.filters];

                return localFilters.sort(
                    /** Tri avec une comparaison selon l'Id de ListItem */
                    function (a: ListItem, b: ListItem) {
                        if (a.Id > b.Id)
                            return 1;
                        if (a.Id < b.Id)
                            return -1;
                        return 0;
                    }).slice(-lastFilters);
            }
            else
                return this.filters;
        }
    }

    /** Affiche les noeuds dépréciés ou non */
    public get GetHistoricMode(): boolean {
        return this.isHistoricMode;
    }

    /** Retourne la liste des ids de noeuds cochés */
    public get CheckedNodeIds(): string[] {
        let nodeIds: string[] = [];

        for (let node of this.nodes) {
            if (node.Checked)
                nodeIds.push(node.Id);
        }

        return nodeIds;
    }

    /** Retourne la liste des noeuds impactés */
    public get InvolvedNodes(): TreeNode[] {
        return this.involvedNodes;
    }

    /** Renvoi le grouping sélectionné */
    public get GetGrouping(): FilterT {
        return this.grouping;
    }

    /** 
     * Retourne le mode courant
     * @todo Virer ce truc pourri ==> NE PLUS UTILISER D'ENUM !!!!!!!!!!
     * @description Ajout de ? dans this.selectModeStrategy?.GetSelectMode() sinon erreur à la déconnexion dans Workbench.vue à la déconnexion (après Reset du store)
     */
    public get GetSelectMode(): SelectModeEnum {
        return (this.selectModeStrategy) ? this.selectModeStrategy?.GetSelectMode() : SelectModeEnum.Single;
    }

    public get StopPropagation() {
        return (isContextMenu: boolean, node: TreeNode): boolean => {
            return this.selectModeStrategy?.StopPropagation(isContextMenu, node);
        }
    }

    /** Détermine si le bouton Historique est visible ou non */
    public get HasHistoricMode(): boolean {
        return this.hasHistoricMode
    }

    /** Détermine si le bouton de Filtre par propriété est visible ou non */
    public get HasPropertiesFilter(): boolean {
        return this.hasPropertiesFilter;
    }

    /** Détermine si le bouton affiché que les éléments sélectionnés est visible ou non */
    public get HasToggleCheckOnly(): boolean {
        return this.hasToggleCheckOnly;
    }

    /** Détermine si le bouton sélectionné tout est visible ou non */
    public get HasSelectAll(): boolean {
        return this.hasSelectAll;
    }

    /** Détermine si le bouton désélectionné tout est visible ou non */
    public get HasClearSelection(): boolean {
        return this.hasClearSelection;
    }

    /** Détermine si la fenêtre de Filtre par propriétés est affichée ou non */
    public get PropertiesFilterVisible(): boolean {
        return this.propertiesFilterVisible;
    }

    /** Liste de ObjectTypes affiché dans le propertie filter */
    public get PropertiesFilterObjectTypes(): string[]{
        return this.propertiesFilterObjectTypes;
    }
}

export default FilterTreeStore;
